export type CanonicalCreateDecision = 'REQUEST_CREATED' | 'REQUEST_PENDING';

export type PendingPairDirection = 'outgoing' | 'incoming' | 'unknown';

export type CanonicalCreateRequestRow = {
  success?: boolean;
  decision?: string | null;
  request_id?: string | null;
  sender_id?: string | null;
  recipient_id?: string | null;
  error?: string | null;
};

export type CanonicalMappedRequest = {
  id: string | null;
  status: string;
  decision: CanonicalCreateDecision;
  sender_id: string | null;
  recipient_id: string | null;
};

export type CanonicalCreateRequestResult = {
  data: CanonicalMappedRequest[] | null;
  error: { message: string; code: string } | null;
};

export function isDuplicateRequestError(
  error: { code?: string; message?: string } | null | undefined
): boolean {
  if (!error) return false;
  return error.code === '23505' || String(error.message || '').toLowerCase().includes('duplicate');
}

export function resolvePendingPairDirection(args: {
  currentUserId: string;
  targetUserId: string;
  senderId?: string | null;
  recipientId?: string | null;
}): PendingPairDirection {
  const { currentUserId, targetUserId, senderId, recipientId } = args;
  if (!currentUserId || !targetUserId || !senderId || !recipientId) return 'unknown';
  if (senderId === currentUserId && recipientId === targetUserId) return 'outgoing';
  if (senderId === targetUserId && recipientId === currentUserId) return 'incoming';
  return 'unknown';
}

export type PendingConnectUi =
  | { kind: 'outgoing' }
  | { kind: 'incoming' }
  | { kind: 'unknown' };

export function decidePendingConnectUi(args: {
  currentUserId: string;
  targetUserId: string;
  senderId?: string | null;
  recipientId?: string | null;
}): PendingConnectUi {
  const direction = resolvePendingPairDirection(args);
  if (direction === 'outgoing') return { kind: 'outgoing' };
  if (direction === 'incoming') return { kind: 'incoming' };
  return { kind: 'unknown' };
}

export function mapCanonicalCreateRequestResult(
  row: CanonicalCreateRequestRow | null | undefined,
  fallback: { senderId: string; receiverId: string }
): CanonicalCreateRequestResult {
  const decision = row?.decision;

  if (decision === 'REQUEST_PENDING') {
    return {
      data: [
        {
          id: row?.request_id ?? null,
          status: 'REQUEST_PENDING',
          decision: 'REQUEST_PENDING',
          sender_id: row?.sender_id ?? null,
          recipient_id: row?.recipient_id ?? null,
        },
      ],
      error: null,
    };
  }

  if (!row?.success) {
    return {
      data: null,
      error: {
        message: row?.error || 'Failed to send request',
        code: decision === 'ALREADY_FRIENDS' ? 'ALREADY_FRIENDS' : 'REQUEST_FAILED',
      },
    };
  }

  return {
    data: [
      {
        id: row.request_id ?? null,
        status: decision || 'REQUEST_CREATED',
        decision: 'REQUEST_CREATED',
        sender_id: row.sender_id || fallback.senderId,
        recipient_id: row.recipient_id || fallback.receiverId,
      },
    ],
    error: null,
  };
}

export function getCanonicalCreateDecision(
  result: {
    data?: Array<{ decision?: string }> | null;
    error?: { code?: string; message?: string } | null;
  }
): CanonicalCreateDecision | null {
  const decision = result?.data?.[0]?.decision;
  if (decision === 'REQUEST_CREATED' || decision === 'REQUEST_PENDING') return decision;
  return null;
}
